import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from '../../../utils/useSnackbar';
import { exportAutomationPlan } from '../../../utils/api/auto-execution_apis';
import { STATUS } from '../../../utils/api/sharedfunction';
import { importAutomationPlan, navigateToInstructions } from '../automationStudioNewSlice';
import { downloadJsonExport, parseImportFileContent } from '../planImportExport';

export default function useAutomationStudioNewImportExport({
    testCaseId,
    onRefetchFiles,
}) {
    const dispatch = useDispatch();
    const { showSnackbar } = useSnackbar();
    const importInputRef = useRef(null);
    const [showQuickUploadModal, setShowQuickUploadModal] = useState(false);

    const handleExport = useCallback(async () => {
        if (!testCaseId) {
            showSnackbar('Save or open a test case to export', 'error');
            return;
        }

        const result = await exportAutomationPlan({ testCaseId });
        if (result.status !== STATUS.SUCCESS) {
            showSnackbar(result.error || 'Export failed', 'error');
            return;
        }

        downloadJsonExport(result.data);
        showSnackbar('Plan exported successfully', 'success');
    }, [testCaseId, showSnackbar]);

    const handleImportFile = useCallback(
        (e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const parsed = JSON.parse(event.target.result);
                    const result = parseImportFileContent(parsed);
                    if (!result.ok) {
                        showSnackbar(result.error, 'error');
                        return;
                    }

                    dispatch(
                        importAutomationPlan({
                            instructions: result.instructions,
                            dynamicResult: result.dynamicResult,
                        })
                    );
                    dispatch(navigateToInstructions());

                    const parts = [
                        result.summary.hasInstructionsField &&
                            `${result.summary.instructionCount} instruction${
                                result.summary.instructionCount !== 1 ? 's' : ''
                            }`,
                        result.summary.hasPlanField && 'plan',
                    ].filter(Boolean);
                    showSnackbar(`Imported ${parts.join(' and ')} successfully`, 'success');
                } catch {
                    showSnackbar('Failed to read file: invalid JSON', 'error');
                }
            };
            reader.onerror = () => showSnackbar('Failed to read file', 'error');
            reader.readAsText(file);
            e.target.value = '';
        },
        [dispatch, showSnackbar]
    );

    const handleImportClick = useCallback(() => {
        importInputRef.current?.click();
    }, []);

    const handleUploadSuccess = useCallback(() => {
        onRefetchFiles?.();
        setShowQuickUploadModal(false);
    }, [onRefetchFiles]);

    return {
        importInputRef,
        showQuickUploadModal,
        setShowQuickUploadModal,
        handleExport,
        handleImportFile,
        handleImportClick,
        handleUploadSuccess,
    };
}
